/**
 * Settings -> Phone -> AI calls out ("AI helistab"): the AI placing a call to a
 * company itself.
 *
 * A tab of its own rather than more of the answering AI's page, because the
 * two switches decide different things: that one is whom a machine answers,
 * this one is whom a machine RINGS. The voice, the model and the knowledge are
 * shared and stay on the answering tab; the notes here point back to it.
 *
 * One card: the switch with the server's verdict on whether the AI could
 * really call, test mode and its numbers, the calling number, the limits, the
 * automation's switch, calling window, holidays and gap (R4,
 * `AiOutboundAutomationFields`), whether the AI sends the defect report letter
 * itself (R9, `AiOutboundReportMailFields`), and (R2) the words the AI opens
 * with and what it offers (`AiOutboundOpening`). The retention ages are stated, not edited:
 * they are always on. "Stop everything" and what the automation is doing now
 * are on the AI calls page's automation tab. The company card's "AI test call"
 * button reads the same settings, so a save here drops its cached verdict.
 */
import { useEffect, useState, type FormEvent } from "react";
import { PhoneOutgoing, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../../i18n/I18nProvider";
import {
  getAiOutboundStatus,
  invalidateAiOutboundStatus,
  saveAiOutboundSettings,
  settingsSaveError,
  type AiOutboundStatus,
  type SettingsSaveError,
} from "../../../../../lib/aiOutbound";
import { toOpeningDraft, toOpeningPatch, type OpeningDraft } from "../../../../../lib/aiOutboundOpening";
import { formatPhone } from "../../../../../lib/phone";
import { Btn, StatusPill } from "../../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../../panel/IntegrationCard";
import { Note, SaveRow, ToggleRow } from "../../panel/fields";
import { AiOutboundAutomationFields } from "./AiOutboundAutomationFields";
import { AiOutboundFields } from "./AiOutboundFields";
import { AiOutboundOpening } from "./AiOutboundOpening";
import { AiOutboundReportMailFields } from "./AiOutboundReportMailFields";
import {
  outboundBlockerNotes,
  outboundCardBlockers,
  toOutboundDraft,
  toOutboundPatch,
  type OutboundDraft,
} from "./aiOutboundNotes";

type SaveState = "idle" | "saving" | "saved" | "error";

/** A refused value that is a phone number is shown the way every number in the CRM is. */
function shownValue(error: SettingsSaveError): string {
  return error.field ? formatPhone(error.value) : error.value;
}

export function AiOutboundCard() {
  const t = useT();
  const [status, setStatus] = useState<AiOutboundStatus | null>(null);
  const [draft, setDraft] = useState<OutboundDraft | null>(null);
  const [openingDraft, setOpeningDraft] = useState<OpeningDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null);

  function hydrate(data: AiOutboundStatus) {
    // Always from the server's answer, never from the draft: a field the
    // server did not keep has to snap back where the owner can see it.
    setStatus(data);
    setDraft(toOutboundDraft(data.settings));
    setOpeningDraft(toOpeningDraft(data.settings));
  }

  useEffect(() => {
    let alive = true;
    // With the owned numbers: this is the one screen that picks a calling
    // number, so it is the one that pays for the Twilio round trip.
    getAiOutboundStatus({ withNumbers: true })
      .then((data) => {
        if (alive) hydrate(data);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!draft || !status || !openingDraft) return;
    setSaveState("saving");
    setSaveError(null);
    try {
      hydrate(
        await saveAiOutboundSettings({
          ...toOutboundPatch(draft, status.settings),
          ...toOpeningPatch(openingDraft, status.settings),
        }),
      );
      invalidateAiOutboundStatus();
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch (err) {
      setSaveError(settingsSaveError(err));
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  // What the server is running on, not what is being edited: an unsaved switch
  // must not make a gap look fixed.
  const notes = outboundBlockerNotes(outboundCardBlockers(status));

  return (
    <IntegrationCard
      icon={PhoneOutgoing}
      title={t("settings.aiOutbound.title")}
      desc={t("settings.aiOutbound.desc")}
      status={
        <ConnStatus
          ok={status?.ready ?? false}
          okLabel={t("settings.aiOutbound.statusOn")}
          offLabel={t("settings.aiOutbound.statusOff")}
        />
      }
    >
      {draft && status ? (
        <form onSubmit={handleSave} className="space-y-5">
          <ToggleRow
            label={t("settings.aiOutbound.enable")}
            hint={t("settings.aiOutbound.enableHint")}
            checked={draft.enabled}
            onChange={(enabled) => setDraft({ ...draft, enabled })}
          />

          {/* Every gap at once, each naming where it is fixed. */}
          {notes.map((note) => (
            <Note key={note.blocker} tone={note.tone}>
              {t(`settings.aiOutbound.blocker.${note.key}`, { code: note.blocker })}
            </Note>
          ))}

          <ToggleRow
            label={t("settings.aiOutbound.testMode")}
            hint={t("settings.aiOutbound.testModeHint")}
            checked={draft.testMode}
            onChange={(testMode) => setDraft({ ...draft, testMode })}
          />
          {/* Said from the saved value: this is the state calls are placed in
              right now, not the one being typed. */}
          {!status.settings.testMode ? <Note tone="warn">{t("settings.aiOutbound.testModeOff")}</Note> : null}

          {/* Not a switch on purpose: the opening sentence tells the person the
              call is recorded, and a switch would make that sentence a lie.
              The ages are the saved ones the retention sweep deletes by
              (`lib/outbound/retention.ts`), the same two the privacy page states. */}
          <Note tone="info">
            {t("settings.aiOutbound.recordedNote")}{" "}
            {t("settings.aiOutbound.retentionNote", {
              recording: status.settings.recordingDays,
              transcript: status.settings.transcriptDays,
            })}
          </Note>

          <AiOutboundFields
            draft={draft}
            onChange={setDraft}
            savedFromNumber={status.settings.fromNumber}
            mainNumber={status.mainNumber}
            ownedNumbers={status.ownedNumbers}
          />

          <AiOutboundAutomationFields draft={draft} onChange={setDraft} />

          <AiOutboundReportMailFields draft={draft} onChange={setDraft} savedLive={status.settings.reportMailLive === true} />

          {openingDraft ? (
            <AiOutboundOpening
              draft={openingDraft}
              onChange={setOpeningDraft}
              defaults={status.openingDefaults ?? { question: "", reasons: { letter: "", call: "", cold: "" } }}
            />
          ) : null}

          {saveError ? (
            <Note tone="bad" live>
              {t(`settings.aiOutbound.error.${saveError.key}`, { value: shownValue(saveError) })}
            </Note>
          ) : null}

          <SaveRow>
            <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
              {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              {saveState === "saving" ? t("settings.aiOutbound.saving") : t("settings.aiOutbound.save")}
            </Btn>
            {saveState === "saved" ? <StatusPill tone="ok">{t("settings.aiOutbound.saved")}</StatusPill> : null}
            {saveState === "error" ? <StatusPill tone="lt">{t("settings.aiOutbound.saveError")}</StatusPill> : null}
          </SaveRow>
        </form>
      ) : (
        // No form over a status that never arrived: saving on top of a row
        // nobody has seen would switch calling on or off blind.
        <Note tone="bad">{t("settings.aiOutbound.loadError")}</Note>
      )}
    </IntegrationCard>
  );
}
